import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Collapse, Alert, Typography, message, Tooltip } from "antd";
import { InfoCircleOutlined, FilePdfOutlined, ColumnHeightOutlined, VerticalAlignMiddleOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem, CheckItem, InputNumberItem } from "./Items";
import SelectUbicacionesProyectoItem from "./SelectUbicacionesProyectoItem";
import VisorRutaPickingMapa from "./VisorRutaPickingMapa";
import TarjetaParadaPicking from "./TarjetaParadaPicking";
import PosicionRackButton from "./PosicionRackButton";
import rutaPickingPdf from "../Pdf/rutaPickingPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const { Text } = Typography;

const llaveInsumo = (proyectoId, insumoId) => `${proyectoId}_${insumoId}`;

const estiloCeldaEncabezado = { border: "1px solid #e8e8e8", padding: "8px 10px", background: "#fafafa", fontWeight: 600, fontSize: 12, textAlign: "left" };
const estiloCelda = { border: "1px solid #e8e8e8", padding: "8px 10px", verticalAlign: "middle" };

//Modal de "Ruta de almacenaje": tras recibir insumos de una Orden de Compra o de una Factura Sin O/C (o
//desde el botón persistente en la tabla correspondiente), junta TODAS las recepciones de ese documento que
//todavía no tengan su ubicación confirmada, agrupadas por almacén. Por insumo: si ya tiene ubicación
//predeterminada se muestra editable igual, pero con un tooltip en vez de la casilla de "guardar como
//predeterminada" (para cambiar la predeterminada hay que editar el insumo); si no tiene, se elige una y
//opcionalmente se guarda como nueva predeterminada. "Generar guía" solo previsualiza la ruta
//(picking/generarRutaGuardado, no muta nada); "Confirmar" es lo único que de verdad reasigna el inventario
//recién recibido a la ubicación elegida
const ModalRutaAlmacenaje = ({ visible, onClose, empresaId, headersToken, documentoOrigenId, documentoOrigenTipo = "OrdenDeCompra" }) => {
  const esFactura = documentoOrigenTipo === "FacturaSinOrdenDeCompra";
  const rutaBase = esFactura ? "facturassinordendecompra" : "ordenesdecompra";
  const campoIdOrigen = esFactura ? "facturaId" : "ordenDeCompraId";

  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [seleccion, setSeleccion] = useState({});
  const [guias, setGuias] = useState({});
  const [confirmando, setConfirmando] = useState(false);

  const cargarDatos = useCallback(() => {
    if (!documentoOrigenId) return;
    setCargando(true);
    axios
      .post(`${rutaBase}/datosParaConfirmarUbicaciones`, { empresaId, [campoIdOrigen]: documentoOrigenId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success === 2) {
          setDatos(res.data);
          const seleccionInicial = {};
          (res.data.almacenes || []).forEach((valorAlmacen) => {
            (valorAlmacen.insumos || []).forEach((valorInsumo) => {
              seleccionInicial[llaveInsumo(valorAlmacen.proyectoId, valorInsumo.insumoId)] = {
                ubicacionId: valorInsumo.ubicacionPredeterminadaId || undefined,
                guardarComoPredeterminada: false,
              };
            });
          });
          setSeleccion(seleccionInicial);
          setGuias({});
        } else {
          setDatos(null);
          if (res.data.success === 1) {
            message.info(res.data.message || "No hay recepciones pendientes de confirmar ubicación");
            onClose();
          } else {
            message.error(res.data.message || "No se pudieron consultar los datos de la recepción");
          }
        }
      })
      .catch(() => message.error("No se pudieron consultar los datos de la recepción"))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, headersToken, documentoOrigenId, rutaBase, campoIdOrigen]);

  useEffect(() => {
    if (visible) cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, documentoOrigenId]);

  const actualizarSeleccion = (proyectoId, insumoId, cambios) => {
    setSeleccion((prev) => ({
      ...prev,
      [llaveInsumo(proyectoId, insumoId)]: { ...prev[llaveInsumo(proyectoId, insumoId)], ...cambios },
    }));
  };

  //Un insumo puede guardarse en UNA ubicación (el caso normal) o DIVIDIDO entre varias (ej. no cabe todo en
  //un solo espacio) - dividir exige al menos 2 renglones, cada uno con ubicación+cantidad, y que la suma
  //cierre exactamente con lo recibido (si no, ni "Generar guía" ni "Confirmar" se habilitan).
  const sumaDivisiones = (divisiones) => (divisiones || []).reduce((acumulado, valorDivision) => acumulado + (Number(valorDivision.cantidad) || 0), 0);

  const seleccionResuelta = (valorSeleccion, cantidadTotal) => {
    if (!valorSeleccion?.dividir) return !!valorSeleccion?.ubicacionId;
    const divisiones = valorSeleccion.divisiones || [];
    if (divisiones.length < 2) return false;
    if (divisiones.some((valorDivision) => !valorDivision.ubicacionId || !(Number(valorDivision.cantidad) > 0))) return false;
    return Math.abs(sumaDivisiones(divisiones) - Number(cantidadTotal)) < 0.001;
  };

  const activarDividir = (proyectoId, insumoId) => {
    actualizarSeleccion(proyectoId, insumoId, {
      dividir: true,
      divisiones: [{ ubicacionId: undefined, cantidad: undefined }, { ubicacionId: undefined, cantidad: undefined }],
    });
  };

  const desactivarDividir = (proyectoId, insumoId, ubicacionPredeterminadaId) => {
    actualizarSeleccion(proyectoId, insumoId, { dividir: false, divisiones: [], ubicacionId: ubicacionPredeterminadaId || undefined });
  };

  const actualizarDivision = (proyectoId, insumoId, indice, cambios) => {
    setSeleccion((prev) => {
      const llave = llaveInsumo(proyectoId, insumoId);
      const actual = prev[llave] || {};
      const divisiones = [...(actual.divisiones || [])];
      divisiones[indice] = { ...divisiones[indice], ...cambios };
      return { ...prev, [llave]: { ...actual, divisiones } };
    });
  };

  const agregarDivision = (proyectoId, insumoId) => {
    setSeleccion((prev) => {
      const llave = llaveInsumo(proyectoId, insumoId);
      const actual = prev[llave] || {};
      return { ...prev, [llave]: { ...actual, divisiones: [...(actual.divisiones || []), { ubicacionId: undefined, cantidad: undefined }] } };
    });
  };

  const quitarDivision = (proyectoId, insumoId, indice) => {
    setSeleccion((prev) => {
      const llave = llaveInsumo(proyectoId, insumoId);
      const actual = prev[llave] || {};
      return { ...prev, [llave]: { ...actual, divisiones: (actual.divisiones || []).filter((_, valorIndice) => valorIndice !== indice) } };
    });
  };

  const almacenResuelto = (almacen) =>
    (almacen.insumos || []).every((valorInsumo) => seleccionResuelta(seleccion[llaveInsumo(almacen.proyectoId, valorInsumo.insumoId)], valorInsumo.cantidad));

  const generarGuia = async (almacen) => {
    if (!almacenResuelto(almacen)) {
      message.warning("Falta elegir la ubicación de algún insumo de este almacén");
      return;
    }
    const proyectoIdStr = almacen.proyectoId.toString();
    setGuias((prev) => ({ ...prev, [proyectoIdStr]: { ...prev[proyectoIdStr], cargando: true } }));

    const itemsGuardar = almacen.insumos.flatMap((valorInsumo) => {
      const valorSeleccion = seleccion[llaveInsumo(almacen.proyectoId, valorInsumo.insumoId)] || {};
      if (valorSeleccion.dividir) {
        return (valorSeleccion.divisiones || []).map((valorDivision) => ({
          insumoId: valorInsumo.insumoId,
          clave: valorInsumo.clave,
          descripcion: valorInsumo.descripcion,
          cantidad: Number(valorDivision.cantidad),
          ubicacionId: valorDivision.ubicacionId,
        }));
      }
      return [{
        insumoId: valorInsumo.insumoId,
        clave: valorInsumo.clave,
        descripcion: valorInsumo.descripcion,
        cantidad: valorInsumo.cantidad,
        ubicacionId: valorSeleccion.ubicacionId,
      }];
    });

    try {
      const res = await axios.post(
        "picking/generarRutaGuardado",
        { empresaId, proyectoId: almacen.proyectoId, itemsGuardar },
        { headers: headersToken }
      );
      if (res.data.success !== 2) {
        message.error(res.data.error || "No se pudo generar la guía de almacenaje");
        setGuias((prev) => ({ ...prev, [proyectoIdStr]: { cargando: false, paradas: [] } }));
        return;
      }

      const paradas = res.data.paradas || [];
      const recolectado = {};
      paradas.forEach((valorParada) => {
        const idParada = valorParada.ubicacionId || valorParada.nodoAccesoId;
        //Se incluye el índice en la llave: un insumo dividido entre varias ubicaciones puede aparecer más
        //de una vez dentro de la MISMA parada (si comparten nodo de acceso) - sin el índice, ambas filas
        //compartirían la misma llave y marcar una marcaría las dos (ver TarjetaParadaPicking.js).
        (valorParada.insumos || []).forEach((valorInsumo, indice) => {
          recolectado[`${idParada}_${valorInsumo.insumoId}_${indice}`] = { checked: false, cantidad: valorInsumo.cantidad };
        });
      });

      setGuias((prev) => ({
        ...prev,
        [proyectoIdStr]: {
          cargando: false,
          paradas,
          paradasCompletas: res.data.paradasCompletas,
          sinNodoAsignado: res.data.sinNodoAsignado || [],
          noAlcanzables: res.data.noAlcanzables,
          mapaAlmacenId: res.data.mapaAlmacenId || null,
          segmentosCamino: res.data.segmentosCamino || [],
          recolectado,
          datosMapa: null,
          cargandoMapa: !!res.data.mapaAlmacenId,
        },
      }));

      if (res.data.mapaAlmacenId) {
        axios
          .post("mapaAlmacen/selectMapaAlmacen", { empresaId, proyectoId: almacen.proyectoId }, { headers: headersToken })
          .then((resMapa) => {
            if (resMapa.data.success === 2) {
              setGuias((prev) => ({
                ...prev,
                [proyectoIdStr]: { ...prev[proyectoIdStr], datosMapa: resMapa.data, cargandoMapa: false },
              }));
            }
          })
          .catch(() => setGuias((prev) => ({ ...prev, [proyectoIdStr]: { ...prev[proyectoIdStr], cargandoMapa: false } })));
      }
    } catch (error) {
      console.error("Error al generar la guía de almacenaje:", error);
      message.error("No se pudo generar la guía de almacenaje");
      setGuias((prev) => ({ ...prev, [proyectoIdStr]: { cargando: false, paradas: [] } }));
    }
  };

  const exportarPdf = (almacen, guia) => {
    const dd = rutaPickingPdf({
      titulo: "Ruta de almacenaje",
      proyectoNombre: almacen.proyectoNombre,
      paradas: guia.paradas,
      faltantes: [],
      sinUbicacionAsignada: [],
      datosMapa: guia.datosMapa,
      segmentosCamino: guia.segmentosCamino,
      etiquetaAccion: "",
    });
    pdfMake.createPdf(dd).download(`RutaAlmacenaje_${(almacen.proyectoNombre || "almacen").replace(/\s+/g, "_")}.pdf`);
  };

  const marcarEnGuia = (proyectoId, llave, checked) => {
    const proyectoIdStr = proyectoId.toString();
    setGuias((prev) => ({
      ...prev,
      [proyectoIdStr]: {
        ...prev[proyectoIdStr],
        recolectado: { ...prev[proyectoIdStr]?.recolectado, [llave]: { ...prev[proyectoIdStr]?.recolectado?.[llave], checked } },
      },
    }));
  };

  const confirmarTodo = () => {
    if (!datos) return;
    const confirmaciones = [];
    for (const valorAlmacen of datos.almacenes) {
      for (const valorInsumo of valorAlmacen.insumos) {
        const valorSeleccion = seleccion[llaveInsumo(valorAlmacen.proyectoId, valorInsumo.insumoId)];
        if (!seleccionResuelta(valorSeleccion, valorInsumo.cantidad)) {
          message.warning("Falta completar la ubicación (o la división entre ubicaciones) de algún insumo");
          return;
        }
        if (valorSeleccion.dividir) {
          confirmaciones.push({
            proyectoId: valorAlmacen.proyectoId,
            insumoId: valorInsumo.insumoId,
            entradaSalidaIds: valorInsumo.entradaSalidaIds,
            divisiones: valorSeleccion.divisiones.map((valorDivision) => ({ ubicacionId: valorDivision.ubicacionId, cantidad: Number(valorDivision.cantidad) })),
          });
        } else {
          confirmaciones.push({
            proyectoId: valorAlmacen.proyectoId,
            insumoId: valorInsumo.insumoId,
            ubicacionId: valorSeleccion.ubicacionId,
            entradaSalidaIds: valorInsumo.entradaSalidaIds,
            cantidad: valorInsumo.cantidad,
            guardarComoPredeterminada: valorInsumo.requiereSeleccion && !!valorSeleccion.guardarComoPredeterminada,
          });
        }
      }
    }

    setConfirmando(true);
    axios
      .post(
        `${rutaBase}/confirmarUbicacionesRecepcion`,
        { empresaId, [campoIdOrigen]: documentoOrigenId, movimientoInventarioIds: datos.movimientoInventarioIds, confirmaciones },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          message.success("Ubicaciones confirmadas: el inventario ya quedó guardado en esas ubicaciones");
          (res.data.advertenciasCapacidad || []).forEach((valorAdvertencia) => {
            message.warning(`La ubicación ${valorAdvertencia.clave} excede su capacidad máxima por ${valorAdvertencia.excedente}`);
          });
          onClose();
        } else {
          message.error(res.data.message || "No se pudieron confirmar las ubicaciones");
        }
      })
      .catch(() => message.error("No se pudieron confirmar las ubicaciones"))
      .finally(() => setConfirmando(false));
  };

  const todoResuelto = !!datos && datos.almacenes.every((valorAlmacen) => almacenResuelto(valorAlmacen));

  return (
    <ModalItem
      title="Ruta de almacenaje"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      spinning={cargando}
    >
      {!datos ? (
        !cargando && (
          <div style={{ padding: "20px 0" }}>
            <Text type="secondary">No hay recepciones pendientes de confirmar ubicación.</Text>
          </div>
        )
      ) : (
        <>
          <Collapse defaultActiveKey={datos.almacenes.map((valorAlmacen) => valorAlmacen.proyectoId)} bordered={false}>
            {datos.almacenes.map((valorAlmacen) => {
              const proyectoIdStr = valorAlmacen.proyectoId.toString();
              const guia = guias[proyectoIdStr];
              return (
                <Collapse.Panel key={valorAlmacen.proyectoId} header={<strong>{valorAlmacen.proyectoNombre}</strong>}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
                      <thead>
                        <tr>
                          <th style={estiloCeldaEncabezado}>Insumo</th>
                          <th style={{ ...estiloCeldaEncabezado, width: 100, textAlign: "center" }}>Cantidad recibida</th>
                          <th style={{ ...estiloCeldaEncabezado, width: 260 }}>Ubicación predeterminada</th>
                          <th style={{ ...estiloCeldaEncabezado, width: 130, textAlign: "center" }}>Activar predeterminada</th>
                          <th style={{ ...estiloCeldaEncabezado, width: 150, textAlign: "center" }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {valorAlmacen.insumos.map((valorInsumo) => {
                          const valorSeleccion = seleccion[llaveInsumo(valorAlmacen.proyectoId, valorInsumo.insumoId)] || {};
                          const sumaActual = sumaDivisiones(valorSeleccion.divisiones);
                          const sumaOk = Math.abs(sumaActual - valorInsumo.cantidad) < 0.001;
                          const puedeDividir = Number(valorInsumo.cantidad) >= 2;
                          return (
                            <React.Fragment key={valorInsumo.insumoId}>
                              <tr>
                                <td style={estiloCelda}>
                                  <strong>{valorInsumo.clave}</strong> - {valorInsumo.descripcion}
                                </td>
                                <td style={{ ...estiloCelda, textAlign: "center" }}>{valorInsumo.cantidad}</td>
                                <td style={estiloCelda}>
                                  {valorSeleccion.dividir ? (
                                    <Text type="secondary" style={{ fontSize: 12 }}>Dividida en {(valorSeleccion.divisiones || []).length} ubicaciones (ver abajo)</Text>
                                  ) : (
                                    <SelectUbicacionesProyectoItem
                                      empresaId={empresaId}
                                      headersToken={headersToken}
                                      proyectoId={valorAlmacen.proyectoId}
                                      value={valorSeleccion.ubicacionId}
                                      onChange={(value) => actualizarSeleccion(valorAlmacen.proyectoId, valorInsumo.insumoId, { ubicacionId: value })}
                                      width="100%"
                                    />
                                  )}
                                </td>
                                <td style={{ ...estiloCelda, textAlign: "center" }}>
                                  {valorSeleccion.dividir ? (
                                    <Text type="secondary">—</Text>
                                  ) : valorInsumo.requiereSeleccion ? (
                                    <CheckItem
                                      checked={!!valorSeleccion.guardarComoPredeterminada}
                                      onChange={(checked) => actualizarSeleccion(valorAlmacen.proyectoId, valorInsumo.insumoId, { guardarComoPredeterminada: checked })}
                                    />
                                  ) : (
                                    <Tooltip title="Para cambiar la ubicación predeterminada de este insumo, edítalo en Insumos.">
                                      <InfoCircleOutlined style={{ color: "#8a93a6" }} />
                                    </Tooltip>
                                  )}
                                </td>
                                <td style={{ ...estiloCelda, textAlign: "center" }}>
                                  {valorSeleccion.dividir ? (
                                    <Tooltip title= "No dividir, usar solo una ubicación">
                                      <ButtonItem danger size="small" icon = {<VerticalAlignMiddleOutlined />} onClick={() => desactivarDividir(valorAlmacen.proyectoId, valorInsumo.insumoId, valorInsumo.ubicacionPredeterminadaId)}/>
                                    </Tooltip>
                                  ) : puedeDividir ? (
                                    <Tooltip title="Dividir entre varias ubicaciones">
                                      <ButtonItem cyan size="small" icon={<ColumnHeightOutlined />} onClick={() => activarDividir(valorAlmacen.proyectoId, valorInsumo.insumoId)} />
                                    </Tooltip>
                                  ) : (
                                    <Text type="secondary">—</Text>
                                  )}
                                </td>
                              </tr>
                              {valorSeleccion.dividir && (
                                <tr>
                                  <td colSpan={5} style={{ ...estiloCelda, background: "#f7f8fa" }}>
                                    {(valorSeleccion.divisiones || []).map((valorDivision, indice) => (
                                      <div key={indice} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                        <Text type="secondary" style={{ fontSize: 12, width: 72, flexShrink: 0 }}>Ubicación {indice + 1}</Text>
                                        <SelectUbicacionesProyectoItem
                                          empresaId={empresaId}
                                          headersToken={headersToken}
                                          proyectoId={valorAlmacen.proyectoId}
                                          value={valorDivision.ubicacionId}
                                          onChange={(value) => actualizarDivision(valorAlmacen.proyectoId, valorInsumo.insumoId, indice, { ubicacionId: value })}
                                          width={220}
                                        />
                                        <InputNumberItem
                                          size="small"
                                          style={{ width: 90 }}
                                          min={0}
                                          placeholder="Cantidad"
                                          value={valorDivision.cantidad}
                                          onChange={(value) => actualizarDivision(valorAlmacen.proyectoId, valorInsumo.insumoId, indice, { cantidad: value })}
                                        />
                                        {(valorSeleccion.divisiones || []).length > 2 && (
                                          <ButtonItem danger size="small" lessTime onClick={() => quitarDivision(valorAlmacen.proyectoId, valorInsumo.insumoId, indice)}>✕</ButtonItem>
                                        )}
                                      </div>
                                    ))}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                                      <ButtonItem cyan size="small" onClick={() => agregarDivision(valorAlmacen.proyectoId, valorInsumo.insumoId)}>
                                        + Agregar ubicación
                                      </ButtonItem>
                                      <Text style={{ fontSize: 12, fontWeight: 600, color: sumaOk ? "#389e0d" : "#cf1322" }}>
                                        Asignado: {sumaActual} / {valorInsumo.cantidad}
                                      </Text>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <ButtonItem onClick={() => generarGuia(valorAlmacen)} loading={guia?.cargando} disabled={!almacenResuelto(valorAlmacen)} style={{ marginBottom: 16 }}>
                    Generar guía
                  </ButtonItem>

                  {guia && !guia.cargando && Array.isArray(guia.paradas) && (
                    <div style={{ marginTop: 8 }}>
                      {guia.sinNodoAsignado?.length > 0 && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message="Algunas ubicaciones no tienen un nodo de acceso configurado en el mapa"
                          description={guia.sinNodoAsignado.map((i) => `${i.clave} - ${i.descripcion}: ${i.cantidad}`).join(", ")}
                        />
                      )}
                      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                        <ButtonItem vino icon={<FilePdfOutlined />} onClick={() =>  exportarPdf(valorAlmacen, guia)}>
                          Imprimir
                        </ButtonItem>
                      </div>
                      <VisorRutaPickingMapa
                        titulo="Ruta de almacenaje - mapa"
                        mapaAlmacenId={guia.mapaAlmacenId}
                        datosMapa={guia.datosMapa}
                        cargando={guia.cargandoMapa}
                        segmentosCamino={guia.segmentosCamino}
                        paradas={guia.paradas}
                        indiceParadaActual={null}
                        recolectado={guia.recolectado}
                        marcar={(llave, checked) => marcarEnGuia(valorAlmacen.proyectoId, llave, checked)}
                        cambiarCantidad={() => {}}
                        accionesInsumo={(insumo) => (
                          <PosicionRackButton empresaId={empresaId} headersToken={headersToken} proyectoId={valorAlmacen.proyectoId} insumo={insumo} />
                        )}
                      />
                      {guia.paradas.map((valorParada) => {
                        const idParada = valorParada.ubicacionId || valorParada.nodoAccesoId;
                        return (
                          <TarjetaParadaPicking
                            key={idParada}
                            parada={valorParada}
                            recolectado={guia.recolectado}
                            marcar={(llave, checked) => marcarEnGuia(valorAlmacen.proyectoId, llave, checked)}
                            cambiarCantidad={() => {}}
                            cantidadEditable={false}
                            accionesInsumo={(insumo) => (
                              <PosicionRackButton
                                empresaId={empresaId}
                                headersToken={headersToken}
                                proyectoId={valorAlmacen.proyectoId}
                                insumo={insumo}
                              />
                            )}
                          />
                        );
                      })}
                    </div>
                  )}
                </Collapse.Panel>
              );
            })}
          </Collapse>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
            <ButtonItem danger onClick={onClose}>Cerrar</ButtonItem>
            <ButtonItem verde type="primary" onClick={confirmarTodo} loading={confirmando} disabled={!todoResuelto}>
              Confirmar
            </ButtonItem>
          </div>
        </>
      )}
    </ModalItem>
  );
};

export default ModalRutaAlmacenaje;
